import { useState, useEffect } from "react";
import {
  IconUpload,
  IconRefresh,
  IconEye,
  IconAlertTriangle,
  IconDownload,
  IconPlayerPlay,
  IconFileZip,
  IconArchive,
  IconLoader2,
  IconX,
  IconChevronLeft,
  IconChevronRight,
} from "@tabler/icons-react";
import api from "../lib/api";
import MainCard from "../components/MainCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";

interface UploadedMonth {
  year: number;
  month: number;
  count: number;
  uploaded_at: string;
}

interface CommissionRecord {
  id: number;
  store_id: number | null;
  store_distributor_name: string | null;
  sale_store_id: number | null;
  amount: number | null;
  i_customer: number | null;
  request_id: string | null;
  advice_id: string | null;
  redeemed: boolean;
  transaction_created_at: string | null;
  markup_at: string | null;
  markup_status: string | null;
  transaction_matched_at: string | null;
  matched_status: string | null;
  transaction_redeemed_at: string | null;
  type: string | null;
  transaction_type: string | null;
  product_name: string | null;
  spar_total_ogr_commission: number | null;
  spar_co_commission: number | null;
  spar_retail_ogr_commission: number | null;
  spar_retail_voucher_commission: number | null;
  unredeemed_voucher_commission: number | null;
  redeemed_out_of_period_voucher_commission: number | null;
  non_spar_mobile_customer_commission: number | null;
}

function getDefaultMonth(): string {
  const now = new Date();
  // Default to previous month (e.g. "2026-02" when current is March 2026)
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}`;
}

function getDateRangeForMonth(monthValue: string): {
  startDate: string;
  endDate: string;
} {
  const [year, month] = monthValue.split("-").map(Number);
  // Start date: first day of the selected month (e.g. Mar 1 for Mar selection)
  const startDate = new Date(year, month - 1, 1);
  // End date: last day of selected month (e.g. Mar 31 for Mar selection)
  const endDate = new Date(year, month, 0);
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return { startDate: fmt(startDate), endDate: fmt(endDate) };
}

function generateMonthOptions(): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  const now = new Date();
  // Show last 12 months
  for (let i = 1; i <= 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
    });
    options.push({ value, label });
  }
  return options;
}

export function RetailerCommissionsPage() {
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploadedMonths, setUploadedMonths] = useState<UploadedMonth[]>([]);
  const [loadingMonths, setLoadingMonths] = useState(false);
  const [replaceDialog, setReplaceDialog] = useState<{
    open: boolean;
    year: number;
    month: number;
  }>({
    open: false,
    year: 0,
    month: 0,
  });
  const [detailsDialog, setDetailsDialog] = useState<{
    open: boolean;
    year: number;
    month: number;
  }>({
    open: false,
    year: 0,
    month: 0,
  });
  const [detailsData, setDetailsData] = useState<CommissionRecord[]>([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsPage, setDetailsPage] = useState(1);
  const [detailsTotal, setDetailsTotal] = useState(0);
  const [detailsLimit] = useState(100);

  // Commission Reconciliation state
  const [reconMonth, setReconMonth] = useState(getDefaultMonth);
  const [reconGenerating, setReconGenerating] = useState(false);
  const [reconZipFile, setReconZipFile] = useState<string | null>(null);
  const [reconFileCount, setReconFileCount] = useState(0);
  const [reconError, setReconError] = useState("");
  const [reconSuccess, setReconSuccess] = useState("");
  const [downloadingZip, setDownloadingZip] = useState<string | null>(null);
  const [archivingZip, setArchivingZip] = useState<string | null>(null);
  const [previousZips, setPreviousZips] = useState<
    { name: string; size: number; updated: string }[]
  >([]);
  const [previousZipsLoading, setPreviousZipsLoading] = useState(false);

  useEffect(() => {
    loadUploadedMonths();
    loadPreviousZips();
  }, []);

  useEffect(() => {
    if (detailsDialog.open) {
      loadMonthDetails(detailsDialog.year, detailsDialog.month, detailsPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadMonthDetails is not wrapped in useCallback; year/month are stable while dialog is open
  }, [
    detailsDialog.open,
    detailsDialog.year,
    detailsDialog.month,
    detailsPage,
  ]);

  const loadUploadedMonths = async () => {
    setLoadingMonths(true);
    try {
      const res = await api.get("/spar-mobile/retailer-commissions/months");
      setUploadedMonths(res.data.data || []);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load uploaded months");
      setTimeout(() => setError(""), 5000);
    } finally {
      setLoadingMonths(false);
    }
  };

  const loadMonthDetails = async (
    year: number,
    month: number,
    page: number,
  ) => {
    setDetailsLoading(true);
    try {
      const res = await api.get(
        `/spar-mobile/retailer-commissions/months/${year}/${month}`,
        {
          params: { page, limit: detailsLimit },
        },
      );
      setDetailsData(res.data.data || []);
      setDetailsTotal(res.data.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load month details");
      setTimeout(() => setError(""), 5000);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadFile(file);
      setError("");
      setSuccess("");
    }
  };

  // Note: Month checking is now done on the backend based on transaction_matched_at dates
  // We'll let the backend determine the month and return an error if it already exists

  const handleUpload = async (replaceExisting: boolean = false) => {
    if (!uploadFile) {
      setError("Please select a file first");
      return;
    }

    setUploading(true);
    setError("");
    setSuccess("");

    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      if (replaceExisting) {
        formData.append("replaceExisting", "true");
      }

      // Try upload - backend will determine month from transaction_matched_at
      // and return error if month exists and replaceExisting is false
      const res = await api.post(
        "/spar-mobile/retailer-commissions/upload",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      setSuccess(res.data.message || "File uploaded successfully");
      setUploadFile(null);
      const fileInput = document.getElementById(
        "commission-upload",
      ) as HTMLInputElement;
      if (fileInput) fileInput.value = "";

      await loadUploadedMonths();

      setTimeout(() => setSuccess(""), 5000);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || "Failed to upload file";

      if (errorMessage.includes("already exists") && !replaceExisting) {
        const match = errorMessage.match(/(\d{4})-(\d{2})/);
        if (match) {
          const year = parseInt(match[1], 10);
          const month = parseInt(match[2], 10);
          setReplaceDialog({
            open: true,
            year,
            month,
          });
          setUploading(false);
          return;
        }
      }

      setError(errorMessage);
      setTimeout(() => setError(""), 10000);
    } finally {
      setUploading(false);
    }
  };

  const handleReplaceConfirm = () => {
    setReplaceDialog({ open: false, year: 0, month: 0 });
    handleUpload(true);
  };

  const handleViewDetails = (year: number, month: number) => {
    setDetailsDialog({ open: true, year, month });
    setDetailsPage(1);
  };

  const formatMonth = (year: number, month: number) => {
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString("en-US", { year: "numeric", month: "long" });
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Commission Reconciliation handlers
  const loadPreviousZips = async () => {
    setPreviousZipsLoading(true);
    try {
      const res = await api.get(
        "/spar-mobile/commission-reconciliation/zip-files",
      );
      if (res.data.success) {
        setPreviousZips(res.data.files || []);
      }
    } catch (err: any) {
      console.error("Failed to load previous ZIP files:", err);
    } finally {
      setPreviousZipsLoading(false);
    }
  };

  const handleGenerateRecon = async () => {
    if (!reconMonth) {
      setReconError("Please select a month");
      return;
    }
    const { startDate, endDate } = getDateRangeForMonth(reconMonth);
    setReconGenerating(true);
    setReconError("");
    setReconSuccess("");
    setReconZipFile(null);
    try {
      const res = await api.post("/spar-mobile/commission-reconciliation", {
        startDate,
        endDate,
      });
      if (res.data.success) {
        const fileCount = res.data.gcsUris?.length || 0;
        setReconFileCount(fileCount);
        setReconZipFile(res.data.zipFile || null);
        setReconSuccess(
          `Generated ${fileCount} CSV files and ZIP archive successfully`,
        );
        await loadPreviousZips();
      } else {
        setReconError(res.data.error || "Failed to generate reconciliation");
      }
    } catch (err: any) {
      setReconError(
        err.response?.data?.message ||
          "Failed to generate reconciliation reports",
      );
    } finally {
      setReconGenerating(false);
    }
  };

  const handleDownloadZip = async (zipFilename: string) => {
    setDownloadingZip(zipFilename);
    try {
      const res = await api.get(
        "/spar-mobile/commission-reconciliation/download",
        { params: { file: zipFilename } },
      );
      if (res.data.success && res.data.url) {
        const link = document.createElement("a");
        link.href = res.data.url;
        link.download = zipFilename;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setReconError(res.data.error || "Failed to get download URL");
      }
    } catch (err: any) {
      setReconError(
        err.response?.data?.message || "Failed to download ZIP file",
      );
    } finally {
      setDownloadingZip(null);
    }
  };

  const handleArchiveZip = async (zipFilename: string) => {
    setArchivingZip(zipFilename);
    try {
      const res = await api.post(
        "/spar-mobile/commission-reconciliation/archive",
        { file: zipFilename },
      );
      if (res.data.success) {
        setPreviousZips((prev) =>
          prev.filter((zip) => zip.name !== zipFilename),
        );
      } else {
        setReconError(res.data.error || "Failed to archive ZIP file");
      }
    } catch (err: any) {
      setReconError(
        err.response?.data?.message || "Failed to archive ZIP file",
      );
    } finally {
      setArchivingZip(null);
    }
  };

  const formatZipDate = (dateStr: string) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-ZA", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const totalPages = Math.ceil(detailsTotal / detailsLimit);

  return (
    <div>
      {/* Upload Section */}
      <MainCard className="mb-6">
        <div className="flex flex-col gap-4">
          <h3 className="text-base font-bold">Upload Commission CSV</h3>
          <div className="flex flex-row items-center gap-4">
            <Button
              variant="outline"
              disabled={uploading}
              nativeButton={false}
              render={<label htmlFor="commission-upload" className="cursor-pointer inline-flex items-center" />}
            >
                <IconUpload className="mr-2 h-4 w-4" />
                Select File
                <input
                  id="commission-upload"
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={handleFileSelect}
                />
            </Button>
            {uploadFile && (
              <span className="text-sm text-muted-foreground">
                {uploadFile.name}
              </span>
            )}
            <Button
              onClick={() => handleUpload(false)}
              disabled={!uploadFile || uploading}
            >
              {uploading ? (
                <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <IconUpload className="mr-2 h-4 w-4" />
              )}
              {uploading ? "Uploading..." : "Upload"}
            </Button>
            <Button
              variant="outline"
              onClick={loadUploadedMonths}
              disabled={loadingMonths}
            >
              <IconRefresh className="mr-2 h-4 w-4" />
              Refresh
            </Button>
          </div>
          {error && (
            <Alert variant="destructive">
              <AlertDescription className="flex items-center justify-between gap-2">
                <span>{error}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => setError("")}
                  aria-label="Dismiss"
                >
                  <IconX className="h-4 w-4" />
                </Button>
              </AlertDescription>
            </Alert>
          )}
          {success && (
            <Alert variant="success">
              <AlertDescription className="flex items-center justify-between gap-2">
                <span>{success}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => setSuccess("")}
                  aria-label="Dismiss"
                >
                  <IconX className="h-4 w-4" />
                </Button>
              </AlertDescription>
            </Alert>
          )}
        </div>
      </MainCard>

      {/* Uploaded Months Table */}
      <MainCard>
        <h3 className="mb-4 text-base font-bold">Uploaded Months</h3>
        {loadingMonths ? (
          <div className="flex justify-center p-6">
            <IconLoader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : uploadedMonths.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">
            No months uploaded yet. Upload a CSV file to get started.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Month</TableHead>
                <TableHead>Records</TableHead>
                <TableHead>Last Uploaded</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {uploadedMonths.map((month) => (
                <TableRow key={`${month.year}-${month.month}`}>
                  <TableCell>
                    <span className="text-sm font-medium">
                      {formatMonth(month.year, month.month)}
                    </span>
                    <br />
                    <span className="text-xs text-muted-foreground">
                      {month.year}-{String(month.month).padStart(2, "0")}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {month.count.toLocaleString()}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatDate(month.uploaded_at)}</TableCell>
                  <TableCell className="text-right">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger
                          render={
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() =>
                                handleViewDetails(month.year, month.month)
                              }
                            />
                          }
                        >
                          <IconEye className="h-4 w-4" />
                        </TooltipTrigger>
                        <TooltipContent>View Details</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </MainCard>

      {/* Commission Reconciliation Reports */}
      <Separator className="my-8" />
      <MainCard>
        <div className="flex flex-col gap-4">
          <h3 className="text-base font-bold">
            Commission Reconciliation Reports
          </h3>
          <p className="text-sm text-muted-foreground">
            Generate monthly commission reconciliation CSV files (vouchers,
            rebates, OGR, summary) and download as a single ZIP archive.
          </p>
          <div className="flex flex-col items-end gap-4 sm:flex-row">
            <Select
              value={reconMonth}
              onValueChange={(val) => setReconMonth(val ?? '')}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Select month" />
              </SelectTrigger>
              <SelectContent>
                {generateMonthOptions().map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              onClick={handleGenerateRecon}
              disabled={reconGenerating || !reconMonth}
            >
              {reconGenerating ? (
                <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <IconPlayerPlay className="mr-2 h-4 w-4" />
              )}
              {reconGenerating ? "Generating..." : "Generate Reports"}
            </Button>
          </div>
          {reconError && (
            <Alert variant="destructive">
              <AlertDescription className="flex items-center justify-between gap-2">
                <span>{reconError}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => setReconError("")}
                  aria-label="Dismiss"
                >
                  <IconX className="h-4 w-4" />
                </Button>
              </AlertDescription>
            </Alert>
          )}
          {reconSuccess && (
            <Alert variant="success">
              <AlertDescription className="flex items-center justify-between gap-2">
                <span>{reconSuccess}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => setReconSuccess("")}
                  aria-label="Dismiss"
                >
                  <IconX className="h-4 w-4" />
                </Button>
              </AlertDescription>
            </Alert>
          )}
          {reconZipFile && (
            <div className="flex flex-row items-center gap-4 rounded-md bg-muted p-4">
              <IconFileZip className="h-8 w-8 text-primary" />
              <div className="flex-1">
                <span className="text-sm font-bold">{reconZipFile}</span>
                <br />
                <span className="text-xs text-muted-foreground">
                  {reconFileCount} CSV files included
                </span>
              </div>
              <Button
                onClick={() => handleDownloadZip(reconZipFile!)}
                disabled={downloadingZip !== null}
              >
                {downloadingZip !== null ? (
                  <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <IconDownload className="mr-2 h-4 w-4" />
                )}
                {downloadingZip !== null ? "Downloading..." : "Download ZIP"}
              </Button>
            </div>
          )}
        </div>
      </MainCard>

      {/* Previous ZIP Files */}
      <MainCard className="mt-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-bold">
            Previous Reconciliation Downloads
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={loadPreviousZips}
            disabled={previousZipsLoading}
          >
            <IconRefresh className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>
        {previousZipsLoading ? (
          <div className="flex justify-center p-6">
            <IconLoader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : previousZips.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">
            No previous ZIP files found. Generate a reconciliation report to get
            started.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>File Name</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Generated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {previousZips.map((zip) => (
                <TableRow key={zip.name}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <IconFileZip className="h-4 w-4 text-primary" />
                      <span className="text-sm">{zip.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>{formatFileSize(zip.size)}</TableCell>
                  <TableCell>{formatZipDate(zip.updated)}</TableCell>
                  <TableCell className="text-right">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger
                          render={
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDownloadZip(zip.name)}
                              disabled={downloadingZip === zip.name}
                            />
                          }
                        >
                          {downloadingZip === zip.name ? (
                            <IconLoader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <IconDownload className="h-4 w-4" />
                          )}
                        </TooltipTrigger>
                        <TooltipContent>Download ZIP</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger
                          render={
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleArchiveZip(zip.name)}
                              disabled={archivingZip === zip.name}
                            />
                          }
                        >
                          {archivingZip === zip.name ? (
                            <IconLoader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <IconArchive className="h-4 w-4" />
                          )}
                        </TooltipTrigger>
                        <TooltipContent>Archive</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </MainCard>

      {/* Replace Confirmation Dialog */}
      <Dialog
        open={replaceDialog.open}
        onOpenChange={(open) => {
          if (!open) setReplaceDialog({ open: false, year: 0, month: 0 });
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IconAlertTriangle className="h-5 w-5 text-yellow-500" />
              Replace Existing Data?
            </DialogTitle>
            <DialogDescription>
              Data for {formatMonth(replaceDialog.year, replaceDialog.month)}{" "}
              already exists. Uploading this file will replace all existing data
              for this month. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setReplaceDialog({ open: false, year: 0, month: 0 })
              }
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={handleReplaceConfirm}
            >
              Replace Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialog.open}
        onOpenChange={(open) => {
          if (!open) setDetailsDialog({ open: false, year: 0, month: 0 });
        }}
      >
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>
              Commission Details -{" "}
              {formatMonth(detailsDialog.year, detailsDialog.month)}
            </DialogTitle>
          </DialogHeader>
          {detailsLoading ? (
            <div className="flex justify-center p-6">
              <IconLoader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <div className="max-h-[600px] overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Store ID</TableHead>
                      <TableHead>Distributor</TableHead>
                      <TableHead>Sale Store</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Total Commission</TableHead>
                      <TableHead>Created At</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {detailsData.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell>{record.store_id || "-"}</TableCell>
                        <TableCell>
                          {record.store_distributor_name || "-"}
                        </TableCell>
                        <TableCell>{record.sale_store_id || "-"}</TableCell>
                        <TableCell>
                          {record.amount ? `R${record.amount}` : "-"}
                        </TableCell>
                        <TableCell>{record.i_customer || "-"}</TableCell>
                        <TableCell>{record.product_name || "-"}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {record.type || "-"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {record.spar_total_ogr_commission
                            ? `R${record.spar_total_ogr_commission.toFixed(2)}`
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {formatDate(record.transaction_created_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex items-center justify-between border-t pt-4">
                <span className="text-sm text-muted-foreground">
                  Showing {(detailsPage - 1) * detailsLimit + 1}-
                  {Math.min(detailsPage * detailsLimit, detailsTotal)} of{" "}
                  {detailsTotal}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={detailsPage <= 1}
                    onClick={() => setDetailsPage((p) => p - 1)}
                  >
                    <IconChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm">
                    Page {detailsPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={detailsPage >= totalPages}
                    onClick={() => setDetailsPage((p) => p + 1)}
                  >
                    <IconChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setDetailsDialog({ open: false, year: 0, month: 0 })
              }
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
